import React, { useEffect, useMemo, useState } from 'react';
import StbButton from '../../security-toolbox-ui/StbButton';

type SemNote = {
  id: number | string;
  manager_email?: string | null;
  note?: string | null;
  created?: string | null;
  last_updated?: string | null;
  updated_by?: string | null;
};

interface Props {
  notes: SemNote[];
  currentUserEmail: string;
  onEdit: (id: number | string, note: string) => Promise<void> | void;
  onDelete: (id: number | string) => Promise<void> | void;
}

function normalizeEmail(v?: string | null) {
  return String(v || '').trim().toLowerCase();
}

function formatTimestamp(v?: string | null) {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString();
}

function wasUpdated(n: SemNote) {
  if (!n?.last_updated || !n?.created) return false;
  const c = new Date(n.created).getTime();
  const u = new Date(n.last_updated).getTime();
  if (!Number.isFinite(c) || !Number.isFinite(u)) return false;
  return u > c;
}

export default function PovSemNotesPanel({
                                           notes,
                                           currentUserEmail,
                                           onEdit,
                                           onDelete,
                                         }: Props) {
  const me = useMemo(() => normalizeEmail(currentUserEmail), [currentUserEmail]);

  const [editingId, setEditingId] = useState<number | string | null>(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [hoveredId, setHoveredId] = useState<number | string | null>(null);

  // IMPORTANT: reset editor state whenever notes list changes
  // prevents stale edit UI from previous row/modal opens
  useEffect(() => {
    setEditingId(null);
    setDraft('');
    setBusy(false);
    setHoveredId(null);
  }, [notes]);

  const startEdit = (n: SemNote) => {
    setEditingId(n.id);
    setDraft(String(n.note || ''));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft('');
  };

  const saveEdit = async () => {
    if (editingId == null) return;
    const next = draft.trim();
    if (!next) return;

    try {
      setBusy(true);
      await onEdit(editingId, next);
      setEditingId(null);
      setDraft('');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (id: number | string) => {
    try {
      setBusy(true);
      await onDelete(id);
      if (editingId === id) {
        setEditingId(null);
        setDraft('');
      }
    } finally {
      setBusy(false);
    }
  };

  if (!notes?.length) {
    return <div className="text-sm text-gray-600 italic">No notes yet.</div>;
  }

  return (
    <div className="flex flex-col gap-2">
      {notes.map((n) => {
        const owner = normalizeEmail(n.manager_email);
        const canEdit = !!me && !!owner && me === owner;
        const isEditing = editingId === n.id;

        return (
          <div
            key={String(n.id)}
            className="border rounded p-2"
            onMouseEnter={() => setHoveredId(n.id)}
            onMouseLeave={() => setHoveredId((prev) => (prev === n.id ? null : prev))}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="text-xs text-gray-600">
                <span className="font-semibold">{n.manager_email || 'Unknown'}</span>
                {n.created ? ` • ${formatTimestamp(n.created)}` : ''}
                {wasUpdated(n) ? ` • Updated ${formatTimestamp(n.last_updated)}` : ''}
              </div>

              {canEdit && !isEditing && hoveredId === n.id && (
                <StbButton
                  role="update"
                  size="xsmall"
                  iconOnly
                  variant="text"
                  onClick={() => startEdit(n)}
                  disabled={busy}
                />
              )}
            </div>

            {!isEditing ? (
              <div className="text-sm whitespace-pre-wrap mt-1">{n.note || ''}</div>
            ) : (
              <div className="mt-2 flex flex-col gap-2">
                <textarea
                  className="w-full border rounded p-2 text-sm"
                  rows={4}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  disabled={busy}
                />
                <div className="flex items-center gap-2">
                  <StbButton
                    role="save"
                    size="xsmall"
                    variant="outline"
                    onClick={saveEdit}
                    disabled={busy || !draft.trim()}
                  />
                  <StbButton
                    role="cancel"
                    size="xsmall"
                    variant="text"
                    onClick={cancelEdit}
                    disabled={busy}
                  />
                  <StbButton
                    role="delete"
                    size="xsmall"
                    variant="text"
                    onClick={() => handleDelete(n.id)}
                    disabled={busy}
                  />
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}